import config from 'site.config'

const {
  configuration: { envVars }
} = config

const AuthService = () => ({
  async getAuthToken(endpoint, siteId, cookieService, sessionService) {
    let token = this.getAuthFromCookie(cookieService)
    let deviceId = sessionService.getFromCache('deviceId', '')
    if (token) {
      if (!token?.access && !token?.refresh) {
        token = await this.getTokenFromPhoenix(endpoint, siteId)
      }
      // always
      token = await this.verifyTokenWithPhoenix(
        endpoint,
        siteId,
        token,
        'access',
        true
      )
    } else if (deviceId === '') {
      // no device id info available, perform normal auth fetch
      token = await this.getTokenFromPhoenix(endpoint, siteId)
    } else {
      token = await this.getTokenFromPhoenixUsingDeviceId(
        endpoint,
        siteId,
        deviceId
      )

      let partySource = token?.partySource ?? null
      if (partySource !== null) {
        sessionService.saveToCache('PartySource', partySource)
        if (!sessionService.getFromCache('MemberID', null))
          sessionService.saveToCache('MemberID', token.partyId)
      }

      token = await this.verifyTokenWithPhoenix(
        endpoint,
        siteId,
        token,
        'access',
        true
      )
    }
    return token
  },

  refreshAuthToken(endpoint, siteId, cookieService) {
    let token = this.getAuthFromCookie(cookieService)
    token = this.refreshTokenWithPhoenix(endpoint, token)
    return token
  },

  getAuthFromCookie(cookieService) {
    const token = cookieService.get('authToken') || null
    if (token != null) {
      return JSON.parse(token)
    }
  },

  async getTokenFromPhoenix(endpoint, siteId) {
    const url = `${endpoint}/v2/auth/token`
    // Request options for fetch
    let fetchOptions = {
      method: 'POST',
      body: JSON.stringify({ siteId: siteId, loggedIn: true }),
      headers: {
        'Content-Type': 'application/json'
      }
    }
    try {
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      return data
    } catch (e) {
      console.error('getTokenFromPhoenix:', e.message)
      return false
    }
  },

  async getTokenFromPhoenixUsingDeviceId(endpoint, siteId, deviceId) {
    const url = `${endpoint}/v2/auth/token/device`
    const language = deviceId?.LangUser || navigator.language
    // Request options for fetch
    let fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        siteId: siteId,
        idhBrandId: Number.parseInt(process.env.REACT_APP_IDH_BRANDID),
        deviceId,
        countryCode: deviceId?.Country || navigator.language,
        languageCode: language.split('-')[0]
      })
    }

    try {
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      return data
    } catch (e) {
      console.error('getTokenFromPhoenixUsingDeviceId:', e.message)
      return false
    }
  },

  async verifyTokenWithPhoenix(
    endpoint,
    siteId,
    token,
    type = 'access',
    full_response = false
  ) {
    const url = `${endpoint}/v2/auth/token/verify`
    // Request options for fetch
    let fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access}`
      },
      body: JSON.stringify({
        siteId: siteId,
        type: type,
        token: `${token.access}`
      })
    }

    try {
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      if (data?.message) {
        console.error('verifyTokenWithPhoenix:', data.message)
        return false
      }
      // Return full response or just tokens based on the flag
      return full_response ? data : data.tokens
    } catch (e) {
      console.error('verifyTokenWithPhoenix:', e.message)
      return false
    }
  },

  async refreshTokenWithPhoenix(endpoint, token) {
    try {
      if (!token.refresh) {
        throw new Error('no token generated')
      }

      const url = `${endpoint}/v2/auth/token/refresh`
      // Request options for fetch
      let fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.refresh}`
        },
        body: JSON.stringify({ token: token.refresh })
      }
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      if (data?.message) {
        console.error('refreshTokenWithPhoenix:', data.message)
        return false
      }
      return data
    } catch (e) {
      console.error('refreshTokenWithPhoenix:', e.message)
      return false
    }
  },

  async getUserTokenFromPhoenix(endpoint, siteId, user) {
    try {
      const url = `${endpoint}/v2/auth/token`
      // Request options for fetch
      let fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          siteId: siteId,
          partyId: user.MemberID,
          partyEmailId: user.EmailID,
          role: 'access',
          loggedIn: true
        })
      }
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      return data
    } catch (e) {
      console.error('getUserTokenFromPhoenix:', e.message)
      return false
    }
  },

  // triggerOtp(payload, token) {
  //   let result = this.triggerOtpRequest(payload, token)
  //   return result
  // },

  async triggerOtpRequest(payload, token) {
    try {
      // Get user token
      const baseUrl =
        envVars?.REACT_APP_PHNX_API ?? process.env.REACT_APP_PHNX_API
      const url = baseUrl + '/v2/user/otp'
      if (token.access) {
        // Request options for fetch
        let fetchOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.access}`
          },
          body: JSON.stringify({ ...payload })
        }
        // Perform API request using fetch
        const response = await fetch(url, fetchOptions)
        const data = await response.json()
        return data
      }
    } catch (e) {
      console.error('triggerOtpRequest:', e.message)
      return e.message || 'Trigger OTP Error'
    }
  },
  async getNemlToken(endpoint, payload, token) {
    try {
      const url = `${endpoint}/v2/auth/token`
      // Request options for fetch
      let fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.access}`
        },
        body: JSON.stringify(payload)
      }
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      return data
    } catch (e) {
      console.error('getNemlToken:', e.message)
      return false
    }
  },
  async performLoginWithPassword(payload, token) {
    const baseUrl =
      envVars?.REACT_APP_PHNX_API ?? process.env.REACT_APP_PHNX_API
    try {
      const url = `${baseUrl}/v2/user/login`
      // Request options for fetch
      let fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.access}`
        },
        body: JSON.stringify(payload)
      }
      // Perform API request using fetch
      const response = await fetch(url, fetchOptions)
      const data = await response.json()
      return data
    } catch (e) {
      console.error('performLoginWithPassword:', e.message)
      return false
    }
  }
})

export default AuthService
